"use client"
import { motion } from "framer-motion"
import { ChevronDown, Plus, Trash2, Home, X, Save } from "lucide-react"
import { useEffect, useState, useMemo } from "react"
import Dashboard from "../../components/dashboard/Dashboard"
import { State, City } from "country-state-city"
import axios from "axios"
import { ToastContainer, toast } from "react-toastify"
import config from "../../config"
import ValidationErrorSidebar from '../../components/ui/ValidationErrorSidebar';
import { usePageSave } from '../../contexts/PageSaveContext';
import { validateDeductionsVsGrossForProspectiveUpdate, exceedDeductionErrorMessage } from '../../utils/financialGuards'

const interestDetailsFields = [
  {
    id: "loanTakenFrom", label: "Loan Taken From", option: [
      {
        name: "Bank",
        value: "B"
      }, {
        name: "Institution",
        value: "I"
      }
    ]
  },
  { id: "bankInstitutionName", label: "Bank/Institution Name", type: "text" },
  { id: "accountNo", label: "Account No.", type: "text" },
  { id: "dateOfLoan", label: "Date of Loan", type: "date" },
  { id: "totalLoan", label: "Total Loan Amount", type: "number" },
  { id: "loanOutStanding", label: "Loan Outstanding", type: "number" },
  { id: "interestPayableForthYear", label: "Interest Payable", type: "number" },
]

const createDefaultHouseProperty = (id) => ({
  id,
  houseDetailId: null, // null for new entries
  propertyOwner: "",
  propertyType: "",
  state: "",
  stateCode: "",
  city: "",
  flatArea: "",
  pinCode: "",
  sharePercentage: "",
  coOwners: [{ id: 1, name: "", panCardNo: "", aadhaarCardNo: "", percentageOfShare: "" }],
  tenants: [{ id: 1, name: "", panCardNo: "", aadhaarCardNo: "" }],
  houseLoanDetails: [
    {
      id: 1,
      houseLoanDetailId: null,
      loanTakenFrom: "",
      bankInstitutionName: "",
      accountNo: "",
      dateOfLoan: "",
      totalLoan: 0,
      loanOutStanding: 0,
      interestPayableForthYear: 0,
    },
  ],
  housePropertyIncome: {
    housePropertyIncomeId: null,
    grossRentReceived: 0,
    unrealizedRent: 0,
    taxPaidLocalAuth: 0,
    annualValue: 0,
    standardDeduction: 0,
    interestPayable: 0,
    ArrearsUnrealizedRentReceived: 0,
    totalIncomeOfHP: 0,
  },
})

export default function HouseProperty() {
  const { markPageAsSaved, markPageAsUnsaved } = usePageSave()
  const [houseProperties, setHouseProperties] = useState([createDefaultHouseProperty(1)])
  const [activeTab, setActiveTab] = useState(1)
  const [states, setStates] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})

  // Calculate property income using useMemo
  const propertyCalculations = useMemo(() => {
    // First calculate raw values for each property
    const rawCalculations = houseProperties.map((property) => {
      const income = property.housePropertyIncome
      const grossRent = Math.max(0, Number.parseFloat(income.grossRentReceived || "0"))
      const unrealizedRent = Math.max(0, Number.parseFloat(income.unrealizedRent || "0"))
      const taxPaid = Math.max(0, Number.parseFloat(income.taxPaidLocalAuth || "0"))
      const arrears = Math.max(0, Number.parseFloat(income.ArrearsUnrealizedRentReceived || "0"))

      // Annual Value = Gross Rent - Unrealized Rent - Tax Paid (minimum 0)
      const annualValue = Math.max(0, grossRent - unrealizedRent - taxPaid)

      // Standard Deduction = 30% of Annual Value (minimum 0)
      const standardDeduction = Math.max(0, annualValue * 0.3)

      // Interest Payable = Sum of all loan interests (minimum 0)
      const interestPayable = Math.max(
        0,
        property.houseLoanDetails.reduce(
          (sum, loan) => sum + Math.max(0, Number.parseFloat(loan.interestPayableForthYear || "0")),
          0,
        ),
      )

      // Old Regime: Total Income = Annual Value - Standard Deduction - Interest Payable + Arrears
      const oldRegimeTotalIncome = annualValue - standardDeduction - interestPayable + arrears;

      // New Regime: For self-occupied properties, interest payable is 0
      const newRegimeInterestPayable = property.propertyType === "S" ? 0 : interestPayable;
      const newRegimeTotalIncome = annualValue - standardDeduction - newRegimeInterestPayable + arrears;

      return {
        id: property.id,
        annualValue,
        standardDeduction,
        interestPayable,
        oldRegimeTotalIncome,
        newRegimeTotalIncome,
        newRegimeInterestPayable,
      }
    })

    // Calculate total raw income across all properties for both regimes
    const totalOldRegimeIncome = rawCalculations.reduce((sum, calc) => sum + calc.oldRegimeTotalIncome, 0)
    const totalNewRegimeIncome = rawCalculations.reduce((sum, calc) => sum + calc.newRegimeTotalIncome, 0)

    // Apply the -200,000 cap at the total level for both regimes
    const cappedOldRegimeIncome = Math.max(-200000, totalOldRegimeIncome)
    const cappedNewRegimeIncome = Math.max(-200000, totalNewRegimeIncome)

    // Distribute the cap proportionally to each property for both regimes
    const applyCap = (totalIncome, cappedIncome, calculations) => {
      if (totalIncome < -200000) {
        const ratio = cappedIncome / totalIncome
        return calculations.map(calc => ({
          ...calc,
          totalIncomeOfHP: calc.oldRegimeTotalIncome * ratio
        }))
      }
      return calculations.map(calc => ({
        ...calc,
        totalIncomeOfHP: calc.oldRegimeTotalIncome
      }))
    }

    // For now, we'll use old regime calculation for the main display
    // The comparison bar will show both regimes separately
    return applyCap(totalOldRegimeIncome, cappedOldRegimeIncome, rawCalculations)
  }, [houseProperties])

  useEffect(() => {
    // localStorage.setItem("house_properties", propertyCalculations.reduce((sum, calc) => sum + calc.totalIncomeOfHP, 0).toFixed(2))
  }, [propertyCalculations])

  const getCalculationsForProperty = (propertyId) => {
    return (
      propertyCalculations.find((calc) => calc.id === propertyId) || {
        annualValue: 0,
        standardDeduction: 0,
        interestPayable: 0,
        oldRegimeTotalIncome: 0,
        newRegimeTotalIncome: 0,
        newRegimeInterestPayable: 0,
        totalIncomeOfHP: 0,
      }
    )
  }

  useEffect(() => {
    setStates(State.getStatesOfCountry("IN"))
    fetchHouseProperties()
  }, [])

  const fetchHouseProperties = async () => {
    try {
      const response = await axios.get(
        `${config.apiUrl}/income/rental/${localStorage.getItem("PAN_NO")}/${localStorage.getItem("YEAR")}`,
      )
      const data = response.data

      if (data.houseDetails && data.houseDetails.length > 0) {
        const mappedProperties = data.houseDetails.map((house, index) => ({
          ...createDefaultHouseProperty(index + 1),
          houseDetailId: house.houseDetailId,
          propertyType: house.typeOfHP || "",
          sharePercentage: house.shareOwned || "",
          flatArea: house.residenceNo || "",
          state: house.cityOrTownOrDistrict || "",
          stateCode: house.stateCode || "",
          city: house.cityOrTownOrDistrict || "",
          pinCode: house.pinCode || "",
          coOwners: house.coOwners?.length
            ? house.coOwners.map((owner, idx) => ({
              id: idx + 1,
              name: owner.name || "",
              panCardNo: owner.panCardNo || "",
              aadhaarCardNo: owner.aadhaarCardNo || "",
              percentageOfShare: owner.percentageOfShare || "",
            }))
            : [{ id: 1, name: "", panCardNo: "", aadhaarCardNo: "", percentageOfShare: "" }],
          tenants: house.tenants?.length
            ? house.tenants.map((tenant, idx) => ({
              id: idx + 1,
              name: tenant.name || "",
              panCardNo: tenant.panCardNo || "",
              aadhaarCardNo: tenant.aadhaarCardNo || "",
            }))
            : [{ id: 1, name: "", panCardNo: "", aadhaarCardNo: "" }],
          houseLoanDetails: house.houseLoanDetails?.length
            ? house.houseLoanDetails.map((loan, idx) => ({
              id: idx + 1,
              houseLoanDetailId: loan.houseLoanDetailId,
              loanTakenFrom: loan.loanTakenFrom || "",
              bankInstitutionName: loan.bankInstitutionName || "",
              accountNo: loan.accountNo || "",
              dateOfLoan: loan.dateOfLoan || "",
              totalLoan: loan.totalLoan || 0,
              loanOutStanding: loan.loanOutStanding || 0,
              interestPayableForthYear: loan.interestPayableForthYear || 0,
            }))
            : [
              {
                id: 1,
                houseLoanDetailId: null,
                loanTakenFrom: "",
                bankInstitutionName: "",
                accountNo: "",
                dateOfLoan: "",
                totalLoan: 0,
                loanOutStanding: 0,
                interestPayableForthYear: 0,
              },
            ],
          housePropertyIncome: {
            housePropertyIncomeId: house.housePropertyIncome?.housePropertyIncomeId || null,
            grossRentReceived: house.housePropertyIncome?.grossRentReceived || 0,
            unrealizedRent: 0, // Not in API response, keeping as 0
            taxPaidLocalAuth: house.housePropertyIncome?.taxPaidLocalAuth || 0,
            annualValue: house.housePropertyIncome?.annualValue || 0,
            standardDeduction: house.housePropertyIncome?.standardDeduction || 0,
            interestPayable: house.housePropertyIncome?.interestPayable || 0,
            ArrearsUnrealizedRentReceived: house.housePropertyIncome?.ArrearsUnrealizedRentReceived || 0,
            totalIncomeOfHP: house.housePropertyIncome?.totalIncomeOfHP || 0,
          },
        }))

        setHouseProperties(mappedProperties)
        setActiveTab(1)
      }
    } catch (error) {
      console.log("Error fetching house properties:", error)
      toast.error("Failed to fetch house property data.")
    }
  }

  const addHouseProperty = () => {
    const newId = Math.max(...houseProperties.map((p) => p.id)) + 1
    const newProperty = createDefaultHouseProperty(newId)
    setHouseProperties([...houseProperties, newProperty])
    setActiveTab(newId)
  }

  const removeHouseProperty = async (propertyId) => {
    const propertyToRemove = houseProperties.find((property) => property.id === propertyId)

    if (propertyToRemove?.houseDetailId) {
      try {
        await axios.delete(
          `${config.apiUrl}/income/rental/housedetail/${propertyToRemove.houseDetailId}`,
          {
            data: { houseDetailId: propertyToRemove.houseDetailId },
          },
        )
        toast.success("House property deleted successfully!")
      } catch (error) {
        toast.error("Failed to delete house property from server!")
        console.error("Delete error:", error)
        return
      }
    }

    const updatedProperties = houseProperties.filter((property) => property.id !== propertyId)
    setHouseProperties(updatedProperties)
    if (activeTab === propertyId && updatedProperties.length > 0) {
      setActiveTab(updatedProperties[0].id)
    }
  }

  const updateProperty = (propertyId, field, value) => {
    markPageAsUnsaved('house-property')
    setHouseProperties((prev) =>
      prev.map((property) => {
        if (property.id === propertyId) {
          if (field.startsWith("housePropertyIncome.")) {
            const incomeField = field.replace("housePropertyIncome.", "")
            return {
              ...property,
              housePropertyIncome: {
                ...property.housePropertyIncome,
                [incomeField]: value,
              },
            }
          }
          return { ...property, [field]: value }
        }
        return property
      }),
    )
  }

  const addCoOwner = (propertyId) => {
    setHouseProperties((prev) =>
      prev.map((property) =>
        property.id === propertyId
          ? {
            ...property,
            coOwners: [
              ...property.coOwners,
              {
                id: property.coOwners.length + 1,
                name: "",
                panCardNo: "",
                aadhaarCardNo: "",
                percentageOfShare: "",
              },
            ],
          }
          : property,
      ),
    )
  }

  const removeCoOwner = (propertyId, coOwnerId) => {
    setHouseProperties((prev) =>
      prev.map((property) =>
        property.id === propertyId
          ? {
            ...property,
            coOwners: property.coOwners.filter((owner) => owner.id !== coOwnerId),
          }
          : property,
      ),
    )
  }

  const updateCoOwner = (propertyId, coOwnerId, field, value) => {
    setHouseProperties((prev) =>
      prev.map((property) =>
        property.id === propertyId
          ? {
            ...property,
            coOwners: property.coOwners.map((owner) =>
              owner.id === coOwnerId ? { ...owner, [field]: value } : owner,
            ),
          }
          : property,
      ),
    )
  }

  const addTenant = (propertyId) => {
    setHouseProperties((prev) =>
      prev.map((property) =>
        property.id === propertyId
          ? {
            ...property,
            tenants: [
              ...property.tenants,
              {
                id: property.tenants.length + 1,
                name: "",
                panCardNo: "",
                aadhaarCardNo: "",
              },
            ],
          }
          : property,
      ),
    )
  }

  const removeTenant = (propertyId, tenantId) => {
    setHouseProperties((prev) =>
      prev.map((property) =>
        property.id === propertyId
          ? {
            ...property,
            tenants: property.tenants.filter((tenant) => tenant.id !== tenantId),
          }
          : property,
      ),
    )
  }

  const updateTenant = (propertyId, tenantId, field, value) => {
    setHouseProperties((prev) =>
      prev.map((property) =>
        property.id === propertyId
          ? {
            ...property,
            tenants: property.tenants.map((tenant) =>
              tenant.id === tenantId ? { ...tenant, [field]: value } : tenant,
            ),
          }
          : property,
      ),
    )
  }

  // Add a new loan detail
  const addLoanDetail = (propertyId) => {
    setHouseProperties((prev) =>
      prev.map((property) =>
        property.id === propertyId
          ? {
            ...property,
            houseLoanDetails: [
              ...property.houseLoanDetails,
              {
                id: Math.max(...property.houseLoanDetails.map(loan => loan.id), 0) + 1,
                houseLoanDetailId: null,
                loanTakenFrom: "",
                bankInstitutionName: "",
                accountNo: "",
                dateOfLoan: "",
                totalLoan: 0,
                loanOutStanding: 0,
                interestPayableForthYear: 0,
              },
            ],
          }
          : property,
      ),
    )
  }

  // Remove a loan detail
  const removeLoanDetail = (propertyId, loanId) => {
    setHouseProperties((prev) =>
      prev.map((property) =>
        property.id === propertyId
          ? {
            ...property,
            houseLoanDetails: property.houseLoanDetails.filter((loan) => loan.id !== loanId),
          }
          : property,
      ),
    )
  }

  // Update loan detail
  const updateLoanDetail = (propertyId, loanId, field, value) => {
    setHouseProperties((prev) =>
      prev.map((property) =>
        property.id === propertyId
          ? {
            ...property,
            houseLoanDetails: property.houseLoanDetails.map((loan) =>
              loan.id === loanId ? { ...loan, [field]: value } : loan,
            ),
          }
          : property,
      ),
    )

    // Clear validation errors for this specific loan field
    const propertyKey = `property_${propertyId}`;
    const loanKey = `${propertyKey}_loan_${loanId}`;
    clearValidationError(`${loanKey}_${field}`);
  }

  const getCitiesForProperty = (stateCode) => {
    return City.getCitiesOfState("IN", stateCode)
  }

  const constructApiBody = (houseProperties) => {
    const houseDetails = houseProperties.map((property) => {
      const calculations = getCalculationsForProperty(property.id)

      return {
        houseDetailId: property.houseDetailId,
        typeOfHP: property.propertyType,
        shareOwned: property.sharePercentage,
        residenceNo: property.flatArea,
        residenceName: "",
        roadOrStreet: "",
        localityOrArea: "",
        cityOrTownOrDistrict: property.city,
        stateCode: property.stateCode,
        pinCode: property.pinCode,
        zipCode: property.pinCode,
        countryCode: "IN",
        tenants: property.tenants
          .filter((tenant) => tenant.name || tenant.panCardNo || tenant.aadhaarCardNo)
          .map((tenant) => ({
            tenantId: tenant.tenantId || null,
            name: tenant.name,
            panCardNo: tenant.panCardNo,
            aadhaarCardNo: tenant.aadhaarCardNo,
          })),
        coOwners: property.coOwners
          .filter((owner) => owner.name || owner.panCardNo || owner.aadhaarCardNo)
          .map((owner) => ({
            coOwnerId: owner.coOwnerId || null,
            name: owner.name,
            panCardNo: owner.panCardNo,
            aadhaarCardNo: owner.aadhaarCardNo,
            percentageOfShare: owner.percentageOfShare,
          })),
        houseLoanDetails: property.houseLoanDetails
          .filter((loan) => loan.loanTakenFrom || loan.bankInstitutionName || loan.totalLoan > 0)
          .map((loan) => ({
            houseLoanDetailId: loan.houseLoanDetailId,
            loanTakenFrom: loan.loanTakenFrom,
            bankInstitutionName: loan.bankInstitutionName,
            accountNo: loan.accountNo ? loan.accountNo.toString().trim() : "",
            dateOfLoan: loan.dateOfLoan,
            totalLoan: Number.parseFloat(loan.totalLoan || "0"),
            loanOutStanding: Number.parseFloat(loan.loanOutStanding || "0"),
            interestPayableForthYear: Number.parseFloat(loan.interestPayableForthYear || "0"),
          })),
        housePropertyIncome: {
          housePropertyIncomeId: property.housePropertyIncome.housePropertyIncomeId,
          grossRentReceived: Number.parseFloat(property.housePropertyIncome.grossRentReceived || "0"),
          taxPaidLocalAuth: Number.parseFloat(property.housePropertyIncome.taxPaidLocalAuth || "0"),
          annualValue: calculations.annualValue,
          standardDeduction: calculations.standardDeduction,
          interestPayable: calculations.interestPayable,
          ArrearsUnrealizedRentReceived: Number.parseFloat(
            property.housePropertyIncome.ArrearsUnrealizedRentReceived || "0",
          ),
          totalIncomeOfHP: calculations.totalIncomeOfHP,
        },
      }
    })

    return { houseDetails }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate house properties before submission
    if (!validateHouseProperty()) {
      toast.error("Please fix validation errors before submitting");
      return;
    }
    
    try {
      setIsSubmitting(true)
      const body = constructApiBody(houseProperties)
      
      // Debug: Log the request body
      console.log("Request body being sent:", JSON.stringify(body, null, 2));

      // Guard: ensure deductions do not exceed prospective gross after this save
      const guard = await validateDeductionsVsGrossForProspectiveUpdate({
        incomeFromHP: propertyCalculations.reduce((sum, calc) => sum + calc.totalIncomeOfHP, 0) || 0,
      })
      if (!guard.ok) {
        toast.error(exceedDeductionErrorMessage)
        setIsSubmitting(false)
        return
      }

      const response = await axios.patch(
        `${config.apiUrl}/income/rental/${localStorage.getItem("PAN_NO")}/${localStorage.getItem("YEAR")}`,
        body,
      )

      // Update properties with returned IDs
      if (response.data && response.data.houseDetails) {
        setHouseProperties((prev) =>
          prev.map((property, index) => ({
            ...property,
            houseDetailId: response.data.houseDetails[index]?.houseDetailId || property.houseDetailId,
            housePropertyIncome: {
              ...property.housePropertyIncome,
              housePropertyIncomeId:
                response.data.houseDetails[index]?.housePropertyIncome?.housePropertyIncomeId ||
                property.housePropertyIncome.housePropertyIncomeId,
            },
          })),
        )
      }


      const housePropertyAmount = propertyCalculations.reduce((sum, calc) => sum + calc.totalIncomeOfHP, 0)
      localStorage.setItem("house_properties", housePropertyAmount)
      await taxComputation()
      const saved = markPageAsSaved('house-property')
      if (saved) {
        toast.success("House properties updated successfully!")
      } else {
        toast.error("Please save your profile first before saving house property details!")
      }
      setIsSubmitting(false)
    } catch (error) {
      setIsSubmitting(false)
      
      // Debug: Log the full error response
      console.error("Full error response:", error.response);
      console.error("Error data:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      // Handle validation errors from server
      if (error.response?.data?.errors) {
        const serverErrors = {};
        Object.keys(error.response.data.errors).forEach(key => {
          serverErrors[key] = error.response.data.errors[key];
        });
        setValidationErrors(serverErrors);
        toast.error("Please fix the validation errors below");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("Something went wrong!")
      }
      
      console.error("Error:", error)
    }
  }

  const taxComputation = async () => {
    try {
      const pan = localStorage.getItem("PAN_NO");
      const year = localStorage.getItem("YEAR");

      // Calculate house property amounts for both regimes
      const oldRegimeHousePropertyAmount = propertyCalculations.reduce((sum, calc) => sum + calc.oldRegimeTotalIncome, 0)
      const newRegimeHousePropertyAmount = propertyCalculations.reduce((sum, calc) => sum + calc.newRegimeTotalIncome, 0)

      // Apply -200,000 cap for both regimes
      const cappedOldRegimeAmount = Math.max(-200000, oldRegimeHousePropertyAmount)
      const cappedNewRegimeAmount = Math.max(-200000, newRegimeHousePropertyAmount)

      // Fetch both regimes in parallel
      const [oldRes, newRes] = await Promise.all([
        axios.get(`${config.apiUrl}/user/tax-computation/${pan}/${year}/OLD`),
        axios.get(`${config.apiUrl}/user/tax-computation/${pan}/${year}/NEW`)
      ]);

      const prepareRegimeData = (data, regime, amount) => {
        return {
          ...data,
          taxComputationId: data?.taxComputationId || null,
          regime,
          incomeFromHP: amount || 0,
        };
      };

      // Prepare payloads inside `taxComputation` key
      const oldPayload = {
        taxComputation: prepareRegimeData(
          oldRes.data?.taxComputation,
          "OLD",
          cappedOldRegimeAmount
        )
      };

      const newPayload = {
        taxComputation: prepareRegimeData(
          newRes.data?.taxComputation,
          "NEW",
          cappedNewRegimeAmount
        )
      };

      // Update both regimes in parallel
      await Promise.all([
        axios.patch(`${config.apiUrl}/user/tax-computation/${pan}/${year}/OLD`, oldPayload),
        axios.patch(`${config.apiUrl}/user/tax-computation/${pan}/${year}/NEW`, newPayload)
      ]);

    } catch (error) {
      console.error("Error in taxComputation:", error);
    }
  };

  const activeProperty = houseProperties.find((property) => property.id === activeTab)
  const activeCalculations = getCalculationsForProperty(activeTab)
  const isSelfOccupied = (propertyType) => propertyType === "S";
  const handlePropertyTypeChange = (e) => {
    const newType = e.target.value;
    updateProperty(activeProperty.id, "propertyType", newType);

    // If changing to self-occupied, reset the disabled fields to 0
    if (isSelfOccupied(newType)) {
      updateProperty(activeProperty.id, "housePropertyIncome.grossRentReceived", 0);
      updateProperty(activeProperty.id, "housePropertyIncome.unrealizedRent", 0);
      updateProperty(activeProperty.id, "housePropertyIncome.taxPaidLocalAuth", 0);
      // Clear validation errors for this property
      clearValidationError(`property_${activeProperty.id}_gross_rent`);
    } else if (newType === "L" || newType === "D") {
      // Check if gross rent is valid for let out/deemed let out properties
      const grossRent = Number.parseFloat(activeProperty.housePropertyIncome.grossRentReceived || "0");
      if (grossRent <= 0) {
        setValidationErrors(prev => ({
          ...prev,
          [`property_${activeProperty.id}_gross_rent`]: `Property ${activeTab}: Gross rent must be more than zero for ${newType === "L" ? "let out" : "deemed let out"} properties`
        }));
      }
    }
  };
  
  // Validation function for house property
  const validateHouseProperty = () => {
    const errors = {};
    
    houseProperties.forEach((property, index) => {
      const propertyKey = `property_${property.id}`;
      
      // Check if property type is let out or deemed let out
      if (property.propertyType === "L" || property.propertyType === "D") {
        const grossRent = Number.parseFloat(property.housePropertyIncome.grossRentReceived || "0");
        if (grossRent <= 0) {
          errors[`${propertyKey}_gross_rent`] = `Property ${index + 1}: Gross rent must be more than zero for ${property.propertyType === "L" ? "let out" : "deemed let out"} properties`;
        }
      }

      // Validate loan details
      property.houseLoanDetails.forEach((loan, loanIndex) => {
        const loanKey = `${propertyKey}_loan_${loan.id}`;
        
        // If any loan field is filled, validate required fields
        if (loan.loanTakenFrom || loan.bankInstitutionName || loan.totalLoan > 0 || loan.interestPayableForthYear > 0) {
          if (!loan.loanTakenFrom) {
            errors[`${loanKey}_loanTakenFrom`] = `Property ${index + 1}, Loan ${loanIndex + 1}: Please select loan taken from`;
          }
          if (!loan.bankInstitutionName || loan.bankInstitutionName.trim() === "") {
            errors[`${loanKey}_bankInstitutionName`] = `Property ${index + 1}, Loan ${loanIndex + 1}: Bank/Institution name is required`;
          }
          if (!loan.accountNo || loan.accountNo.toString().trim() === "") {
            errors[`${loanKey}_accountNo`] = `Property ${index + 1}, Loan ${loanIndex + 1}: Account number is required`;
          } else {
            // Additional validation for account number format
            const accountNo = loan.accountNo.toString().trim();
            // Check if account number contains only digits and is reasonable length
            if (!/^\d+$/.test(accountNo)) {
              errors[`${loanKey}_accountNo`] = `Property ${index + 1}, Loan ${loanIndex + 1}: Account number must contain only digits`;
            } else if (accountNo.length < 8 || accountNo.length > 20) {
              errors[`${loanKey}_accountNo`] = `Property ${index + 1}, Loan ${loanIndex + 1}: Account number must be between 8-20 digits`;
            }
          }
          if (!loan.dateOfLoan) {
            errors[`${loanKey}_dateOfLoan`] = `Property ${index + 1}, Loan ${loanIndex + 1}: Date of loan is required`;
          }
        }
      });
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Clear validation errors for a specific field
  const clearValidationError = (errorKey) => {
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  };

  // Prepare house property comparison data
  const housePropertyComparisonData = useMemo(() => {
    const totalOldRegimeIncome = propertyCalculations.reduce((sum, calc) => sum + calc.oldRegimeTotalIncome, 0)
    const totalNewRegimeIncome = propertyCalculations.reduce((sum, calc) => sum + calc.newRegimeTotalIncome, 0)
    const totalAnnualValue = propertyCalculations.reduce((sum, calc) => sum + calc.annualValue, 0)
    const totalStandardDeduction = propertyCalculations.reduce((sum, calc) => sum + calc.standardDeduction, 0)
    const totalOldRegimeInterestPayable = propertyCalculations.reduce((sum, calc) => sum + calc.interestPayable, 0)
    const totalNewRegimeInterestPayable = propertyCalculations.reduce((sum, calc) => sum + calc.newRegimeInterestPayable, 0)

    // Apply -200,000 cap for both regimes
    const cappedOldRegimeIncome = Math.max(-200000, totalOldRegimeIncome)
    const cappedNewRegimeIncome = Math.max(-200000, totalNewRegimeIncome)

    const formatCurrency = (value) => {
      return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Math.abs(value))
    }

    return {
      totalHousePropertyIncome: cappedOldRegimeIncome, // For backward compatibility
      totalAnnualValue,
      totalStandardDeduction,
      totalInterestPayable: totalOldRegimeInterestPayable,
      oldRegimeTaxable: cappedOldRegimeIncome,
      newRegimeTaxable: cappedNewRegimeIncome,
      newRegimeInterestPayable: totalNewRegimeInterestPayable,
      difference: cappedNewRegimeIncome - cappedOldRegimeIncome,
      formatCurrency,
    }
  }, [propertyCalculations])

  if (!activeProperty) return null

  return (
    <Dashboard 
      salaryData={housePropertyComparisonData}
      showTaxComparison={true}
    >
      <ToastContainer />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-full p-6 bg-white"
        style={{ borderRadius: "10px" }}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">House Property Management</h2>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={addHouseProperty}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
            >
              <Plus size={20} />
              Add New Property
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`flex items-center justify-center gap-2 px-6 py-2 text-white transition-colors ${isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700"
                }`}
            >
              {isSubmitting ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {isSubmitting ? "Saving..." : "Update All Properties"}
            </motion.button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 border-b border-gray-200">
            {houseProperties.map((property, index) => (
              <div key={property.id} className="relative">
                <button
                  type="button"
                  onClick={() => setActiveTab(property.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${activeTab === property.id
                    ? "bg-cyan-500 text-white border-b-2 border-cyan-500"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  <Home size={16} />
                  Property {index + 1}
                  {houseProperties.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        removeHouseProperty(property.id)
                      }}
                      className="ml-2 p-1 rounded-full hover:bg-red-500 hover:text-white transition-colors"
                    >
                      <X size={14} />
                    </button>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <form onSubmit={handleSubmit} className="space-y-8">
          <motion.div
            key={activeProperty.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Property Type Selection */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">Select Owner of Property</label>
                  <div className="relative">
                    <select
                      className="w-full p-3 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ borderRadius: "10px" }}
                      value={activeProperty.propertyOwner}
                      onChange={(e) => updateProperty(activeProperty.id, "propertyOwner", e.target.value)}
                    >
                      <option value="">Select Owner of Property</option>
                      <option value="self">Self</option>
                      <option value="spouse">Spouse</option>
                      <option value="joint">Joint</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-3.5 text-cyan-500" size={20} />
                  </div>
                </div>
                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">Select Type of House Property</label>
                  <div className="relative">
                    <select
                      className="w-full p-3 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ borderRadius: "10px" }}
                      value={activeProperty.propertyType}
                      onChange={handlePropertyTypeChange}  // Updated handler
                    >
                      <option value="">Select Type of House Property</option>
                      <option value="S">Self Occupied</option>
                      <option value="L">Let Out</option>
                      <option value="D">Deemed let out</option>

                    </select>
                    <ChevronDown className="absolute right-3 top-3.5 text-cyan-500" size={20} />
                  </div>
                </div>
              </div>
            </div>

            {/* Address Section */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="text-lg font-thin text-cyan-500">Address</h4>
              <hr />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">Select State</label>
                  <div className="relative">
                    <select
                      onChange={(e) => {
                        const selectedState = states.find((state) => state.isoCode === e.target.value)
                        updateProperty(activeProperty.id, "stateCode", e.target.value)
                        updateProperty(activeProperty.id, "state", selectedState?.name || "")
                      }}
                      className="w-full p-3 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ borderRadius: "10px" }}
                      value={activeProperty.stateCode}
                    >
                      <option value="">Select State</option>
                      {states?.map((state) => (
                        <option key={state.isoCode} value={state.isoCode}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-3.5 text-cyan-500" size={20} />
                  </div>
                </div>
                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">Town/City</label>
                  <div className="relative">
                    <select
                      className="w-full p-3 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ borderRadius: "10px" }}
                      value={activeProperty.city}
                      onChange={(e) => updateProperty(activeProperty.id, "city", e.target.value)}
                    >
                      <option value="">Town/City</option>
                      {getCitiesForProperty(activeProperty.stateCode)?.map((city) => (
                        <option key={city.name} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-3.5 text-cyan-500" size={20} />
                  </div>
                </div>
                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">Flat No. & Area</label>
                  <input
                    type="text"
                    placeholder="Flat No. & Area"
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{ borderRadius: "10px" }}
                    value={activeProperty.flatArea}
                    onChange={(e) => updateProperty(activeProperty.id, "flatArea", e.target.value)}
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">PIN/ZIP</label>
                  <input
                    type="text"
                    placeholder="PIN/ZIP"
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{ borderRadius: "10px" }}
                    value={activeProperty.pinCode}
                    onChange={(e) => updateProperty(activeProperty.id, "pinCode", e.target.value)}
                  />
                </div>
                <div className="relative">
                  <label className="block text-sm text-gray-600 mb-1">Your % of share in property</label>
                  <input
                    type="text"
                    placeholder="Your % of share in property"
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    style={{ borderRadius: "10px" }}
                    value={activeProperty.sharePercentage}
                    onChange={(e) => updateProperty(activeProperty.id, "sharePercentage", e.target.value)}
                  />
                </div>
              </div>
            </motion.div>

            {/* Co-Owner Details */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-thin text-cyan-500">Co-Owner Details</h4>
                <button
                  type="button"
                  onClick={() => addCoOwner(activeProperty.id)}
                  className="flex items-center gap-2 text-cyan-500 hover:text-cyan-600"
                >
                  <Plus size={20} /> Add Co-Owner
                </button>
              </div>
              <hr />
              {activeProperty.coOwners.map((owner) => (
                <motion.div
                  key={`${activeProperty.id}-coowner-${owner.id}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center"
                >
                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">Name</label>
                    <input
                      type="text"
                      placeholder="Name"
                      className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ borderRadius: "10px" }}
                      value={owner.name}
                      onChange={(e) => updateCoOwner(activeProperty.id, owner.id, "name", e.target.value)}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">PAN</label>
                    <input
                      type="text"
                      placeholder="PAN"
                      className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ borderRadius: "10px" }}
                      value={owner.panCardNo}
                      onChange={(e) => updateCoOwner(activeProperty.id, owner.id, "panCardNo", e.target.value)}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">Aadhaar</label>
                    <input
                      type="text"
                      placeholder="Aadhaar"
                      className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ borderRadius: "10px" }}
                      value={owner.aadhaarCardNo}
                      onChange={(e) => updateCoOwner(activeProperty.id, owner.id, "aadhaarCardNo", e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="relative">
                      <label className="block text-sm text-gray-600 mb-1">% of share</label>
                      <input
                        type="text"
                        placeholder="% of share"
                        className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 flex-1"
                        style={{ borderRadius: "10px" }}
                        value={owner.percentageOfShare}
                        onChange={(e) =>
                          updateCoOwner(activeProperty.id, owner.id, "percentageOfShare", e.target.value)
                        }
                      />
                    </div>
                    {activeProperty.coOwners.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCoOwner(activeProperty.id, owner.id)}
                        className="p-3 text-red-500 hover:text-red-600"
                      >
                        <Trash2 size={20} />
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Tenant Details */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-thin text-cyan-500">Tenant Details</h4>
                <button
                  type="button"
                  onClick={() => addTenant(activeProperty.id)}
                  className="flex items-center gap-2 text-cyan-500 hover:text-cyan-600"
                >
                  <Plus size={20} /> Add Tenant
                </button>
              </div>
              <hr />
              {activeProperty.tenants.map((tenant) => (
                <motion.div
                  key={`${activeProperty.id}-tenant-${tenant.id}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center"
                >
                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">Name</label>
                    <input
                      type="text"
                      placeholder="Name"
                      className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ borderRadius: "10px" }}
                      value={tenant.name}
                      onChange={(e) => updateTenant(activeProperty.id, tenant.id, "name", e.target.value)}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">PAN</label>
                    <input
                      type="text"
                      placeholder="PAN"
                      className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ borderRadius: "10px" }}
                      value={tenant.panCardNo}
                      onChange={(e) => updateTenant(activeProperty.id, tenant.id, "panCardNo", e.target.value)}
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-sm text-gray-600 mb-1">Aadhaar</label>
                    <input
                      type="text"
                      placeholder="Aadhaar"
                      className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      style={{ borderRadius: "10px" }}
                      value={tenant.aadhaarCardNo}
                      onChange={(e) => updateTenant(activeProperty.id, tenant.id, "aadhaarCardNo", e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    {activeProperty.tenants.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTenant(activeProperty.id, tenant.id)}
                        className="p-3 text-red-500 hover:text-red-600"
                      >
                        <Trash2 size={20} />
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Income & Deductions Details */}
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <h4 className="text-lg font-thin text-cyan-500">Income & Deductions Details</h4>
              <hr />
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Gross rent received/receivable/lettable value during the year
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                      <input
                        type="text"
                        className={`w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${isSelfOccupied(activeProperty.propertyType) ? "bg-gray-100 cursor-not-allowed" : ""
                          }`}
                        placeholder="0.00"
                        value={isSelfOccupied(activeProperty.propertyType)
                          ? 0
                          : activeProperty.housePropertyIncome.grossRentReceived
                        }
                        onChange={(e) => {
                        if (!isSelfOccupied(activeProperty.propertyType)) {
                          // Only allow numbers (0-9) and decimal point
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = value.split('.');
                          const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                          const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                          updateProperty(activeProperty.id, "housePropertyIncome.grossRentReceived", numericValue);
                          // Validate gross rent for let out/deemed let out properties
                          if (numericValue > 0) {
                            clearValidationError(`property_${activeProperty.id}_gross_rent`);
                          } else if (activeProperty.propertyType === "L" || activeProperty.propertyType === "D") {
                            setValidationErrors(prev => ({
                              ...prev,
                              [`property_${activeProperty.id}_gross_rent`]: `Property ${activeTab}: Gross rent must be more than zero for ${activeProperty.propertyType === "L" ? "let out" : "deemed let out"} properties`
                            }));
                          }
                        }
                      }}
                      disabled={isSelfOccupied(activeProperty.propertyType)}
                    />
                    </div>
                  </div>

                  {/* Unrealized Rent Field */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      The amount of rent which cannot be realized
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                      <input
                        type="text"
                        className={`w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${isSelfOccupied(activeProperty.propertyType) ? "bg-gray-100 cursor-not-allowed" : ""
                          }`}
                        placeholder="0.00"
                        value={isSelfOccupied(activeProperty.propertyType)
                          ? 0
                          : activeProperty.housePropertyIncome.unrealizedRent
                        }
                        onChange={(e) => {
                          if (!isSelfOccupied(activeProperty.propertyType)) {
                            // Only allow numbers (0-9) and decimal point
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                            const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                            updateProperty(activeProperty.id, "housePropertyIncome.unrealizedRent", numericValue);
                          }
                        }}
                        disabled={isSelfOccupied(activeProperty.propertyType)}
                      />
                    </div>
                  </div>

                  {/* Tax Paid Field */}
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Tax paid to local authorities</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                      <input
                        type="text"
                        className={`w-full pl-8 pr-3 py-3 text-right border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${isSelfOccupied(activeProperty.propertyType) ? "bg-gray-100 cursor-not-allowed" : ""
                          }`}
                        placeholder="0.00"
                        value={isSelfOccupied(activeProperty.propertyType)
                          ? 0
                          : activeProperty.housePropertyIncome.taxPaidLocalAuth
                        }
                        onChange={(e) => {
                          if (!isSelfOccupied(activeProperty.propertyType)) {
                            // Only allow numbers (0-9) and decimal point
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            // Prevent multiple decimal points
                            const parts = value.split('.');
                            const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                            const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                            updateProperty(activeProperty.id, "housePropertyIncome.taxPaidLocalAuth", numericValue);
                          }
                        }}
                        disabled={isSelfOccupied(activeProperty.propertyType)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Annual Value (a - b - c)</label>
                    <input
                      type="number"
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-gray-50"
                      value={activeCalculations.annualValue.toFixed(2)}
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Less: Standard Deduction (30%)</label>
                    <input
                      type="number"
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-gray-50"
                      value={activeCalculations.standardDeduction.toFixed(2)}
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Less: Interest payable on Borrowed Capital
                    </label>
                    <input
                      type="number"
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-gray-50 cursor-pointer"
                      value={activeCalculations.interestPayable.toFixed(2)}
                      onClick={() => setShowModal(true)}
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Add: Arrear of Rent received less 30%</label>
                    <input
                      type="number"
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      value={activeProperty.housePropertyIncome.ArrearsUnrealizedRentReceived}
                      onChange={(e) =>
                        updateProperty(
                          activeProperty.id,
                          "housePropertyIncome.ArrearsUnrealizedRentReceived",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Income From House Property</label>
                    <input
                      type="number"
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-gray-50"
                      value={activeCalculations.totalIncomeOfHP.toFixed(2)}
                      readOnly
                    />
                    {activeCalculations.totalIncomeOfHP < -200000 && (
                      <p className="text-xs text-red-500 mt-1">
                        Note: Actual calculated loss was ₹{Math.abs(activeCalculations.totalIncomeOfHP).toFixed(2)},
                        but is capped at ₹200,000 as per tax rules
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Summary */}
          <div className="mt-6 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-800 mb-2">Summary</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-sm text-gray-600">Total Properties</div>
                <div className="text-xl font-semibold text-cyan-600">{houseProperties.length}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">Total Annual Value</div>
                <div className="text-xl font-semibold text-green-600">
                  ₹{propertyCalculations.reduce((sum, calc) => sum + calc.annualValue, 0).toFixed(2)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">Total Income</div>
                <div className="text-xl font-semibold text-blue-600">
                  ₹{propertyCalculations.reduce((sum, calc) => sum + calc.totalIncomeOfHP, 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Loan Details Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl space-y-4 shadow-lg max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Loan Details</h2>
                <button
                  type="button"
                  onClick={() => addLoanDetail(activeProperty.id)}
                  className="flex items-center gap-2 px-3 py-1 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
                >
                  <Plus size={16} />
                  Add Loan
                </button>
              </div>

              {activeProperty.houseLoanDetails.map((loan, index) => (
                <div key={loan.id} className="border rounded-lg p-4 space-y-4 relative">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium text-gray-700">Loan {index + 1}</h3>
                    {activeProperty.houseLoanDetails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLoanDetail(activeProperty.id, loan.id)}
                        className="p-1 text-red-500 hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {interestDetailsFields.map((field) => (
                      <>
                        {
                          field.option ?
                            <div className="relative">
                              <label className="block text-sm text-gray-600 mb-1">Loan Taken From</label>
                              <div className="relative">
                                <select
                                  className={`w-full p-3 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                                    validationErrors[`property_${activeProperty.id}_loan_${loan.id}_${field.id}`] 
                                      ? 'border-red-500 focus:ring-red-500' 
                                      : 'border-gray-300'
                                  }`}
                                  style={{ borderRadius: "10px" }}
                                  value={loan[field.id] || ""}
                                  onChange={(e) => updateLoanDetail(activeProperty.id, loan.id, field.id, e.target.value)}
                                >

                                  <option value="">Select Loan Taken </option>
                                  {
                                    field.option?.map((e) => {
                                      return (
                                        <>
                                          <option value={e.value}>{e.name}</option>
                                        </>
                                      )
                                    })
                                  }
                                </select>
                                <ChevronDown className="absolute right-3 top-3.5 text-cyan-500" size={20} />
                              </div>
                              {validationErrors[`property_${activeProperty.id}_loan_${loan.id}_${field.id}`] && (
                                <p className="text-xs text-red-500 mt-1">
                                  {validationErrors[`property_${activeProperty.id}_loan_${loan.id}_${field.id}`]}
                                </p>
                              )}
                            </div> :
                            <div key={field.id}>
                              <label className="block text-sm text-gray-600 mb-1">{field.label}</label>
                              <input
                                type={field.type || "text"}
                                className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
                                  validationErrors[`property_${activeProperty.id}_loan_${loan.id}_${field.id}`] 
                                    ? 'border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300'
                                }`}
                                value={loan[field.id]}
                                onChange={(e) => {
                                  if (field.id === "interestPayableForthYear") {
                                    // Only allow numbers (0-9) and decimal point
                                    const value = e.target.value.replace(/[^0-9.]/g, '');
                                    // Prevent multiple decimal points
                                    const parts = value.split('.');
                                    const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                                    const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                                    // Always limit interest to 200,000 regardless of property type
                                    const finalValue = Math.min(numericValue, 200000);
                                    updateLoanDetail(activeProperty.id, loan.id, field.id, finalValue);
                                  } else if (field.id === "accountNo") {
                                    // Only allow digits for account number
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    updateLoanDetail(activeProperty.id, loan.id, field.id, value);
                                  } else if (field.id === "totalLoan" || field.id === "loanOutStanding") {
                                    // Only allow numbers (0-9) and decimal point for amount fields
                                    const value = e.target.value.replace(/[^0-9.]/g, '');
                                    // Prevent multiple decimal points
                                    const parts = value.split('.');
                                    const filteredValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                                    const numericValue = Math.max(0, Number.parseFloat(filteredValue || 0)); // Ensure non-negative
                                    updateLoanDetail(activeProperty.id, loan.id, field.id, numericValue);
                                  } else {
                                    updateLoanDetail(activeProperty.id, loan.id, field.id, e.target.value);
                                  }
                                }}
                                max={field.id === "interestPayableForthYear" ? 200000 : undefined}
                              />
                              {field.id === "interestPayableForthYear" && (
                                <p className="text-xs text-gray-500 mt-1">Max ₹200,000 allowed as per tax rules</p>
                              )}
                              {field.id === "accountNo" && (
                                <p className="text-xs text-gray-500 mt-1">Enter account number (8-20 digits only)</p>
                              )}
                              {validationErrors[`property_${activeProperty.id}_loan_${loan.id}_${field.id}`] && (
                                <p className="text-xs text-red-500 mt-1">
                                  {validationErrors[`property_${activeProperty.id}_loan_${loan.id}_${field.id}`]}
                                </p>
                              )}
                            </div>
                        }
                      </>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex justify-end pt-4 space-x-3">
                <button
                  className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
                <button
                  className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
                  onClick={() => setShowModal(false)}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
      
      {/* Validation Error Sidebar */}
      <ValidationErrorSidebar
        errors={validationErrors}
        isVisible={true}
        onFieldClick={(fieldKey) => {
          // Handle field click to focus on the specific field
          console.log("Field clicked:", fieldKey);
        }}
        title="House Property Validation Errors"
      />
    </Dashboard>
  )
}